"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { ApertureStyles, T, FM, MAXW, SectionEyebrow, RevealGrid, ArrowR, Check, Dash, Half } from "./_shared"
import { useAuth } from "@/lib/auth-context"

const YEARLY_DISCOUNT = 0.20 // 20% off

// Founding-pricing deadline — change this when the cohort closes.
// Set in UTC so the countdown is consistent for every visitor
// regardless of their timezone. The banner hides itself once we
// cross the deadline.
//
// Launch was 2026-05-14, original cohort ran 10 days through
// 2026-05-23. Extended 10 more days on 2026-05-22 — the PH launch
// ended at #92 (no spike) and the cold email funnel needs another
// cycle to convert. New deadline: end of June 2 UTC. Same rationale
// as before (long enough for a second-touch sign-off cycle, short
// enough to still read as urgent), just shifted to match the actual
// distribution timeline we're running.
const FOUNDING_DEADLINE = new Date("2026-06-03T00:00:00Z")

type Tier = {
  n: string
  // Plan key used by /checkout?plan=… (matches VALID_PLANS in checkout/page.tsx)
  planKey: "free" | "pro" | "agency"
  monthly: number
  // Optional anchor: the "regular" price shown struck-through next
  // to the founding price. Set only for tiers that participate in
  // the founding-cohort discount (currently just Pro — Agency stays
  // unanchored so we keep flexibility to revisit its price later
  // without breaking a public promise).
  regularMonthly?: number
  rows: string
  cta: string
  feat: boolean
}

// Cards stay minimal (name + price + rows + CTA). The full feature
// breakdown lives in the FeatureComparison table below.
//
// Founding-cohort anchors: regularMonthly is the price after the
// cohort window closes. Set on both paid tiers so the discount
// story reads symmetric (one anchored tier alone reads as a
// special-case carve-out, both anchored reads as a coherent
// 'founding cohort' offer). $49 → $29 for Pro keeps the monthly
// entry point sharp without leaning on volatile competitor pricing.
// $99 → $79 for Agency stays comfortably under enterprise pricing
// (Mailgun Validate is ~$1k+ for 1M rows) so we still have room
// to raise without breaking competitive positioning later.
const TIERS: Tier[] = [
  { n: "Free",   planKey: "free",   monthly: 0,                       rows: "1,000 rows / month",     cta: "Start free",         feat: false },
  { n: "Pro",    planKey: "pro",    monthly: 29, regularMonthly: 49,  rows: "100,000 rows / month",   cta: "Start with Pro",     feat: true  },
  { n: "Agency", planKey: "agency", monthly: 79, regularMonthly: 99,  rows: "1,000,000 rows / month", cta: "Start with Agency",  feat: false },
]

export default function AperturePricing() {
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly")
  const { user, plan: userPlan } = useAuth()
  // Paid users on this page see a different surface entirely — the
  // tier-picker is for prospects deciding "which plan?". A Pro/Agency
  // user has already answered that question, so the only useful
  // affordance for them is the row top-ups. Everything else
  // (founding banner, billing toggle, tier cards, comparison table)
  // hides for paid sessions.
  const isPaid = !!user && (userPlan === "pro" || userPlan === "agency")

  const fmtPrice = (monthly: number) => {
    if (monthly === 0) return { p: "$0", per: "forever" }
    if (billing === "monthly") return { p: `$${monthly}`, per: "per month" }
    const yearlyMonthly = Math.round(monthly * (1 - YEARLY_DISCOUNT))
    return { p: `$${yearlyMonthly}`, per: "per month, billed yearly" }
  }

  // CTA target depends on auth state + tier:
  // - Free + not authed  → /login?mode=signup
  // - Free + authed      → /dashboard
  // - Paid + not authed  → /login?redirect=/checkout?plan=…
  // - Paid + authed      → /checkout?plan=…
  // - Already on this paid plan → /dashboard/billing (manage)
  const ctaHref = (t: Tier) => {
    if (t.planKey === "free") {
      return user ? "/dashboard" : "/login?mode=signup"
    }
    const planParam = billing === "yearly" ? `${t.planKey}_yearly` : t.planKey
    const checkoutPath = `/checkout?plan=${planParam}`
    if (!user) return `/login?redirect=${encodeURIComponent(checkoutPath)}`
    if (userPlan === t.planKey) return "/dashboard/billing"
    return checkoutPath
  }

  const ctaLabel = (t: Tier) => {
    if (t.planKey === "free") return user ? "Go to dashboard" : t.cta
    if (user && userPlan === t.planKey) return "Manage plan"
    return t.cta
  }

  return (
    <section id="pricing" className="relative">
      <ApertureStyles />
      <div className={`${MAXW} mx-auto px-6 lg:px-10 py-12 sm:py-16 md:py-24`}>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionEyebrow>Pricing</SectionEyebrow>
            <h2
              className="mt-3 leading-[1.0] tracking-[-0.035em] max-w-3xl"
              style={{ fontSize: "clamp(2rem, 4vw, 3.1rem)", fontWeight: 800 }}
            >
              Plain pricing.{" "}
              <em style={{ color: T.acc, fontStyle: "italic", fontWeight: 700 }}>Every core check included.</em>
            </h2>
          </div>
          <div className="flex items-center gap-2 text-[12.5px]" style={{ color: T.ink2 }}>
            <span style={{ width: 6, height: 6, background: T.acc, borderRadius: 999 }} />
            All plans include the cleaner, audit trail, and CSV export.
          </div>
        </div>

        {/* Founding-customer banner — deadline-based instead of seat-count
            so visitors don't need to ask "how many seats are left?". The
            countdown ticks down every second once mounted; banner hides
            itself the moment the deadline passes. */}
        <div data-testid="founding-banner-wrap">
          <FoundingBanner deadline={FOUNDING_DEADLINE} />
        </div>

        {/* Billing toggle — segmented control with a sliding indicator
            so it reads as one connected switch (was rendering as two
            separate buttons where only the active one looked clickable). */}
        <div className="mt-6 flex justify-center">
          <div
            role="tablist"
            aria-label="Billing period"
            className="relative inline-flex items-center p-1 rounded-full"
            style={{
              background: T.bgSoft,
              border: `1px solid ${T.hair}`,
              boxShadow: "0 1px 0 rgba(255,255,255,0.65) inset",
            }}
          >
            {/* Sliding indicator — animates between the two halves */}
            <span
              aria-hidden
              className="absolute top-1 bottom-1 rounded-full transition-all duration-300 ease-out"
              style={{
                left: billing === "monthly" ? 4 : "50%",
                width: "calc(50% - 4px)",
                background: T.surface,
                border: `1px solid ${T.hair2}`,
                boxShadow: "0 1px 0 rgba(255,255,255,0.65) inset, 0 4px 14px -6px rgba(10,10,10,0.18)",
              }}
            />
            <button
              type="button"
              role="tab"
              data-testid="billing-toggle-monthly"
              aria-selected={billing === "monthly"}
              onClick={() => setBilling("monthly")}
              className="relative z-10 px-5 py-2 rounded-full transition-colors cursor-pointer"
              style={{
                color: billing === "monthly" ? T.ink : T.ink2,
                fontSize: 13,
                fontWeight: billing === "monthly" ? 600 : 500,
                minWidth: 100,
              }}
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              data-testid="billing-toggle-yearly"
              aria-selected={billing === "yearly"}
              onClick={() => setBilling("yearly")}
              className="relative z-10 px-5 py-2 rounded-full transition-colors cursor-pointer inline-flex items-center justify-center gap-2"
              style={{
                color: billing === "yearly" ? T.ink : T.ink2,
                fontSize: 13,
                fontWeight: billing === "yearly" ? 600 : 500,
                minWidth: 100,
              }}
            >
              Yearly
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded"
                style={{
                  background: T.accChip,
                  color: T.acc,
                  fontFamily: FM, fontSize: 9.5, fontWeight: 700,
                  letterSpacing: "0.1em", textTransform: "uppercase",
                }}
              >
                −20%
              </span>
            </button>
          </div>
        </div>

        <RevealGrid className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {TIERS.map((t, i) => {
            const priceInfo = fmtPrice(t.monthly)
            // Founding anchor: show the struck-through "regular" price
            // only while the cohort window is still open AND we're on
            // monthly billing. Yearly already has its own discount
            // line ("$X/year · save $Y") and stacking two
            // strikethroughs reads as theatre, not signal. Recomputed
            // once per render (the deadline isn't going to flip
            // mid-session).
            const foundingActive = Date.now() < FOUNDING_DEADLINE.getTime()
            const showAnchor =
              foundingActive &&
              billing === "monthly" &&
              t.regularMonthly != null &&
              t.regularMonthly > t.monthly
            // Mobile order: Pro first (most popular), then Agency, then
            // Free at the bottom. md+ falls back to natural Free → Pro
            // → Agency so the price ladder reads left-to-right.
            const mobileOrderClass =
              t.n === "Pro"    ? "order-1 md:order-none" :
              t.n === "Agency" ? "order-2 md:order-none" :
                                 "order-3 md:order-none"
            return (
            <div
              key={t.n}
              data-reveal
              data-testid={`tier-${t.planKey}`}
              className={`relative apl-card-hover ${mobileOrderClass}`}
              style={{ animationDelay: `${i * 90}ms` }}
            >
              {/* Floating badge — kept OUTSIDE the card's overflow-hidden
                  wrapper so it can sit above the top edge without getting
                  clipped (was rendering as a cut-off dark sliver before).

                  When the visitor is logged in AND on this tier, the
                  badge swaps to "Current plan" — that's more relevant
                  to them than the marketing label and removes the awkward
                  case where Pro reads "Most popular" while they ARE on
                  Pro. Visual treatment stays identical (same emerald
                  pill, same drop-shadow). */}
              {(() => {
                const isCurrent = !!user && userPlan === t.planKey
                const showBadge = isCurrent || t.feat
                if (!showBadge) return null
                return (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 sm:left-7 sm:translate-x-0 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                    style={{
                      background: T.acc, color: "#FAF7EE",
                      fontFamily: FM, fontSize: 10, fontWeight: 700,
                      letterSpacing: "0.16em", textTransform: "uppercase",
                      boxShadow: "0 8px 20px -8px rgba(15,81,50,0.55)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isCurrent && (
                      <span
                        aria-hidden
                        style={{
                          width: 5, height: 5, borderRadius: 999,
                          background: "#A8E0BF",
                          boxShadow: "0 0 6px rgba(168,224,191,0.85)",
                        }}
                      />
                    )}
                    {isCurrent ? "Current plan" : "Most popular"}
                  </span>
                )
              })()}
              <div
                className="relative rounded-2xl p-7 flex flex-col overflow-hidden h-full"
                style={{
                  background: t.feat ? T.bgInk : T.surface,
                  color:      t.feat ? "#FAF7EE" : T.ink,
                  border: `1px solid ${t.feat ? T.bgInk : T.hair}`,
                  boxShadow: t.feat
                    ? `0 30px 80px -30px rgba(10,10,10,0.45), 0 0 0 1px rgba(255,255,255,0.06) inset`
                    : `0 1px 0 rgba(255,255,255,0.6) inset, 0 16px 48px -30px rgba(10,10,10,0.18)`,
                  minHeight: 280,
                }}
              >
              {t.feat && (
                <div
                  aria-hidden
                  className="absolute inset-y-0 -left-1/2 w-1/2 pointer-events-none"
                  style={{
                    background: `linear-gradient(90deg, transparent, rgba(110,231,183,0.20), transparent)`,
                    animation: "apl-shimmer-x 5s ease-in-out infinite",
                    filter: "blur(10px)",
                    opacity: 0.6,
                  }}
                />
              )}
              <div className="flex items-baseline justify-between">
                <span
                  style={{
                    fontFamily: FM, fontSize: 11, letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: t.feat ? "#A8E0BF" : T.ink3,
                    fontWeight: 700,
                  }}
                >
                  {t.n}
                </span>
              </div>
              {/* Price row — standard SaaS anchor pattern:
                  ~~$49~~  $29  per month
                  the strikethrough sits inline on the LEFT, large
                  enough to act as the visual anchor (~2/3 the size
                  of the actual price). actual price stays the
                  headline. tiny "FOUNDING" pill below ties the
                  discount back to the cohort. */}
              <div className="mt-6 flex items-baseline gap-3 flex-wrap">
                {showAnchor && (
                  <span
                    data-testid={`tier-${t.planKey}-regular-price`}
                    className="tabular-nums"
                    aria-label={`Regular price $${t.regularMonthly}`}
                    style={{
                      fontSize: "clamp(1.7rem, 2.6vw, 2.2rem)",
                      fontWeight: 700,
                      letterSpacing: "-0.03em",
                      textDecoration: "line-through",
                      textDecorationThickness: "2px",
                      color: t.feat ? "rgba(250,247,238,0.40)" : T.ink3,
                    }}
                  >
                    ${t.regularMonthly}
                  </span>
                )}
                <span
                  data-testid={`tier-${t.planKey}-price`}
                  className="tabular-nums"
                  style={{
                    fontSize: "clamp(2.6rem, 4vw, 3.4rem)",
                    fontWeight: 800,
                    letterSpacing: "-0.04em",
                    color: t.feat ? "#A8E0BF" : T.ink,
                  }}
                >
                  {priceInfo.p}
                </span>
                <span style={{ fontSize: 13, color: t.feat ? "rgba(250,247,238,0.55)" : T.ink2 }}>{priceInfo.per}</span>
              </div>
              {showAnchor && (
                <p
                  className="mt-1.5"
                  style={{
                    fontFamily: FM,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: t.feat ? "#A8E0BF" : T.acc,
                  }}
                >
                  Founding price · locked in for life
                </p>
              )}
              {billing === "yearly" && t.monthly > 0 && (
                <p
                  className="mt-3 tabular-nums"
                  style={{
                    fontSize: 12,
                    color: t.feat ? "rgba(250,247,238,0.55)" : T.ink3,
                    fontFamily: FM,
                    letterSpacing: "0.04em",
                  }}
                >
                  ${Math.round(t.monthly * 12 * (1 - YEARLY_DISCOUNT))}/year{" "}
                  <span style={{ margin: "0 0.5em" }}>·</span>
                  save ${Math.round(t.monthly * 12 * YEARLY_DISCOUNT)}
                </p>
              )}
              <p className="mt-5" style={{ fontSize: 13.5, color: t.feat ? "rgba(250,247,238,0.65)" : T.ink2 }}>{t.rows}</p>

              {/* No bullet list — the FeatureComparison table below is
                  the source of truth for what each tier includes. The
                  card stays minimal: name, price, rows, CTA. */}

              <Link
                href={ctaHref(t)}
                data-testid={`tier-${t.planKey}-cta`}
                className="mt-12 inline-flex items-center justify-center gap-2 transition-all"
                style={{
                  background: t.feat ? T.acc : T.bgInk,
                  color: "#FAF7EE",
                  padding: "0.9rem 1.2rem",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {ctaLabel(t)} <ArrowR />
              </Link>
              </div>
            </div>
            )
          })}
        </RevealGrid>

        {/* ── Row top-ups — paid users only ─────────────────────────
            Tier cards stay visible for everyone (paid users get a
            "Current plan" badge on their tier — see ctaLabel + the
            card-level highlight). This block adds an additional
            paid-only affordance: "buy more rows on top of your
            current cycle without changing plan". Free / unauth
            visitors never see it. */}
        {isPaid && <RowTopUps />}

        {/* ── Feature comparison ─────────────────────────────────────
            Detailed table that resolves "which tier is right for me?"
            without forcing the user to compare three cards visually.
            Mirrors the vendor compare pattern: cream surface, hairline
            grid, Pro column highlighted in accSoft, mobile scroll. */}
        <FeatureComparison />
      </div>
    </section>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   FoundingBanner — limited-time offer with a live countdown.
   Renders nothing pre-hydration (avoids SSR/CSR flash with mismatched
   timestamps) and unmounts entirely once the deadline passes.
   ─────────────────────────────────────────────────────────────────────────── */

function FoundingBanner({ deadline }: { deadline: Date }) {
  const [now, setNow] = useState<Date | null>(null)

  useEffect(() => {
    setNow(new Date())
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])

  if (!now) {
    // Reserve the layout space pre-hydration so the page doesn't jump.
    return <div className="mt-8 h-[68px]" aria-hidden />
  }

  const msLeft = deadline.getTime() - now.getTime()
  if (msLeft <= 0) return null

  const days    = Math.floor(msLeft / (1000 * 60 * 60 * 24))
  const hours   = Math.floor((msLeft / (1000 * 60 * 60)) % 24)
  const minutes = Math.floor((msLeft / (1000 * 60)) % 60)
  const seconds = Math.floor((msLeft / 1000) % 60)

  const pad = (n: number) => n.toString().padStart(2, "0")

  return (
    <div
      className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl px-4 py-3"
      style={{
        background: T.accSoft,
        border: `1px solid rgba(15,81,50,0.20)`,
      }}
    >
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md flex-shrink-0"
        style={{
          background: T.acc, color: "#FAF7EE",
          fontFamily: FM, fontSize: 9.5, fontWeight: 700,
          letterSpacing: "0.16em", textTransform: "uppercase",
        }}
      >
        <span
          style={{
            width: 5, height: 5, background: "#A8E0BF",
            borderRadius: 999, animation: "apl-pulse 1.8s infinite",
          }}
        />
        Founding price
      </span>

      <p style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>
        <span style={{ color: T.ink, fontWeight: 600 }}>$29 / mo locked in for life.</span>{" "}
        Sign up before the offer ends.
      </p>

      {/* Live countdown — the four chip blocks are tabular-nums so the
          digits don't jitter as seconds tick. Days collapse to "—"
          gracefully if you ever set a deadline less than 24h out. */}
      <div data-testid="founding-countdown" className="ml-auto flex items-center gap-1.5 tabular-nums">
        <Block n={days}    label="d" testid="countdown-days" />
        <Block n={hours}   label="h" pad={pad} testid="countdown-hours" />
        <Block n={minutes} label="m" pad={pad} testid="countdown-minutes" />
        <Block n={seconds} label="s" pad={pad} testid="countdown-seconds" />
      </div>
    </div>
  )
}

function Block({ n, label, pad, testid }: { n: number; label: string; pad?: (n: number) => string; testid?: string }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-baseline gap-0.5 px-1.5 py-0.5 rounded-md"
      style={{
        background: T.surface,
        border: `1px solid ${T.hair}`,
      }}
    >
      <span
        style={{
          fontFamily: FM, fontSize: 13, fontWeight: 700,
          color: T.ink, letterSpacing: "-0.01em",
        }}
      >
        {pad ? pad(n) : n}
      </span>
      <span
        style={{
          fontFamily: FM, fontSize: 9.5, fontWeight: 600,
          color: T.ink3, letterSpacing: "0.06em", textTransform: "uppercase",
        }}
      >
        {label}
      </span>
    </span>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   FeatureComparison — detailed feature × tier table.
   Grouped into 5 sections (Limits, Validation, Output, Workspace, Support)
   so users can scan by category instead of staring at one giant flat grid.
   Pro column gets the soft-accent background to anchor the recommended tier.
   Mobile: scrolls horizontally with a minWidth so values don't squeeze.
   ─────────────────────────────────────────────────────────────────────────── */

type CellValue = boolean | "partial" | string

// Only rows that map to real, enforced behavior in the codebase. Aspirational
// features (suppression list, JSON/Parquet, public API, SSO, multi-workspace,
// SLA, dedicated CSM, response-time SLAs) were dropped — promising what we
// can't enforce backfires the first time a customer audits.
const FEATURE_GROUPS: { group: string; rows: { l: string; v: [CellValue, CellValue, CellValue] }[] }[] = [
  {
    group: "Limits",
    rows: [
      { l: "Rows per month",        v: ["1,000",   "100,000",   "1,000,000"] },
      { l: "Max file size",         v: ["10 MB",   "200 MB",    "1 GB"] },
      { l: "Downloads per day",     v: ["1",       "Unlimited", "Unlimited"] },
    ],
  },
  {
    group: "Validation",
    rows: [
      { l: "9 checks in parallel",     v: [true, true, true] },
      { l: "Live MX lookup",           v: [true, true, true] },
      { l: "Catch-all review",         v: [true, true, true] },
      { l: "Disposable & role check",  v: [true, true, true] },
      { l: "TLD trust scoring",        v: [true, true, true] },
      { l: "Typo suggestions",         v: ["View only", true, true] },
    ],
  },
  {
    group: "Tools",
    rows: [
      { l: "CSV export",                 v: [true, true, true] },
      { l: "Per-row audit trail",        v: [true, true, true] },
      { l: "List comparison",            v: ["3 / month", "Unlimited", "Unlimited"] },
      { l: "Column mapper (ESP-aware)",  v: [false, true, true] },
      { l: "Webhook on completion",      v: [false, true, true] },
      { l: "Public API (Bearer auth)",   v: [false, true, true] },
    ],
  },
  {
    group: "Performance",
    rows: [
      { l: "Processing queue",  v: ["Standard", "Standard", "Priority"] },
    ],
  },
  {
    group: "Support",
    rows: [
      { l: "Bug reports & feature requests", v: [true,    true,    true] },
      { l: "AI chat (instant answers)",      v: [true,    true,    true] },
      { l: "Human email follow-up",          v: [false,   "< 48h", "< 24h"] },
    ],
  },
]

/* ───────────────────────────────────────────────────────────────────────────
   RowTopUps — one-time row packs visible to paid users on the pricing
   page. Mirrors what /dashboard/billing offers, but surfaced here so
   the pricing surface answers both audiences at once:

     - free / unauth visitor: sees the 3 tier cards (focused "pick one")
     - paid user (pro / agency): sees the 3 tier cards + this top-up
       block, so the page is useful even after they upgraded.

   Two packs (25k @ $9, 100k @ $29) — same SKUs the billing page
   exposes. Buttons hard-route to /checkout?plan=rows-{pack} to reuse
   the existing checkout flow.
   ─────────────────────────────────────────────────────────────────────────── */

const ROW_PACKS = [
  { pack: "25k",  label: "25,000 rows",  price: 9,  rows: 25_000 },
  { pack: "100k", label: "100,000 rows", price: 29, rows: 100_000 },
] as const

function RowTopUps() {
  return (
    <div className="mt-10 rounded-2xl overflow-hidden" style={{ background: T.surface, border: `1px solid ${T.hair}`, boxShadow: `0 1px 0 rgba(255,255,255,0.65) inset` }}>
      <div className="relative px-6 py-5 md:px-8 md:py-6">
        {/* Mint hairline accent — the Aperture-Light signature, same
            as every other card on the home. */}
        <div
          aria-hidden
          className="absolute inset-x-8 top-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${T.acc} 50%, transparent)` }}
        />

        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <SectionEyebrow>Top-ups</SectionEyebrow>
            <h3
              className="mt-2 leading-[1.15] tracking-[-0.02em]"
              style={{ fontSize: "clamp(1.3rem, 2vw, 1.6rem)", fontWeight: 800, color: T.ink }}
            >
              Running low this month?{" "}
              <em style={{ color: T.acc, fontStyle: "italic", fontWeight: 700 }}>
                Add more without changing plan.
              </em>
            </h3>
            <p className="mt-2" style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.55, maxWidth: "52ch" }}>
              One-time row packs that stack on top of your monthly allowance.
              Spent at the end of the cycle, never expire mid-cycle.
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ROW_PACKS.map(({ pack, label, price, rows }) => {
            const perRow = ((price / rows) * 1000).toFixed(2)
            return (
              <div
                key={pack}
                className="group relative overflow-hidden rounded-xl"
                style={{
                  background: T.surface2,
                  border: `1px solid ${T.hair}`,
                  transition: "border-color 150ms ease",
                }}
              >
                <div className="flex items-center gap-4 p-4">
                  <div
                    className="flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{
                      width: 40, height: 40,
                      background: T.accSoft,
                      border: `1px solid rgba(15,81,50,0.18)`,
                      color: T.acc,
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p style={{ fontSize: 14, fontWeight: 700, color: T.ink, letterSpacing: "-0.005em" }}>{label}</p>
                      <span style={{ fontFamily: FM, fontSize: 10.5, color: T.ink3 }} className="tabular-nums">
                        ${perRow}/1k rows
                      </span>
                    </div>
                    <p className="mt-0.5" style={{ fontSize: 11.5, color: T.ink3 }}>
                      Added to the current billing cycle
                    </p>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span style={{ fontSize: 18, fontWeight: 800, color: T.ink, letterSpacing: "-0.025em" }} className="tabular-nums">
                      ${price}
                    </span>
                    <Link
                      href={`/checkout?plan=rows-${pack}`}
                      className="inline-flex items-center justify-center transition-colors"
                      style={{
                        padding: "7px 14px",
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 700,
                        background: T.accChip,
                        color: T.acc,
                        border: `1px solid rgba(15,81,50,0.30)`,
                        letterSpacing: "0.01em",
                      }}
                    >
                      Buy
                    </Link>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <p className="mt-4" style={{ fontSize: 11, color: T.ink3, fontFamily: FM, letterSpacing: "0.04em" }}>
          ↳ Need a bigger pack or a recurring add-on? Email{" "}
          <a href="mailto:support@databridge.so?subject=Custom%20row%20pack" className="underline underline-offset-2" style={{ color: T.acc }}>
            support@databridge.so
          </a>{" "}
          and we&rsquo;ll quote your volume.
        </p>
      </div>
    </div>
  )
}

function FeatureComparison() {
  const tiers = ["Free", "Pro", "Agency"]
  return (
    <div className="mt-20">
      <SectionEyebrow>Feature comparison</SectionEyebrow>
      <h3
        className="mt-3 leading-[1.1] tracking-[-0.025em] max-w-2xl"
        style={{ fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)", fontWeight: 700 }}
      >
        Everything in every tier, side by side.
      </h3>

      {/* Mobile fallback — the full grid below has a minWidth of 720
          which forces ~340px of horizontal scroll on iPhone SE / 320-class
          phones (read as broken at audit). On phones we stack the tiers
          as cards with their feature checklists. Same data, no scroll.
          Above md the original wide grid keeps doing its job. */}
      <div className="md:hidden mt-8 space-y-3">
        {tiers.map((tierName, ti) => {
          const isPro = ti === 1
          return (
            <div
              key={tierName}
              className="rounded-2xl px-5 py-5"
              style={{
                background: isPro ? T.accSoft : T.surface,
                border: `1px solid ${isPro ? "rgba(15,81,50,0.22)" : T.hair}`,
                boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset, 0 18px 50px -30px rgba(10,10,10,0.18)`,
              }}
            >
              <div className="flex items-center gap-2">
                <span style={{ fontSize: 16, fontWeight: 800, color: isPro ? T.acc : T.ink, letterSpacing: "-0.01em" }}>
                  {tierName}
                </span>
                {isPro && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded"
                    style={{ fontFamily: FM, fontSize: 9.5, background: T.accChip, color: T.acc, letterSpacing: "0.1em", textTransform: "uppercase" }}
                  >
                    popular
                  </span>
                )}
              </div>
              {FEATURE_GROUPS.map((g) => (
                <div key={g.group} className="mt-4">
                  <p style={{ fontFamily: FM, fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: T.ink3, fontWeight: 700 }}>
                    <span style={{ color: T.acc, marginRight: 6 }}>—</span>
                    {g.group}
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {g.rows.map((r) => {
                      const v = r.v[ti]
                      const isBool   = typeof v === "boolean"
                      const isPartial = v === "partial"
                      return (
                        <li key={r.l} className="flex items-center gap-2 text-sm">
                          <span style={{ flex: "0 0 16px" }}>
                            {isBool ? (v ? <Check /> : <Dash />) : isPartial ? <Half /> : null}
                          </span>
                          <span style={{ color: T.ink2, flex: 1 }}>{r.l}</span>
                          {!isBool && !isPartial && (
                            <span style={{ fontSize: 13, fontWeight: 600, color: isPro ? T.acc : T.ink, whiteSpace: "nowrap" }}>
                              {v as string}
                            </span>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )
        })}
      </div>

      <div
        className="hidden md:block mt-8 rounded-2xl overflow-x-auto overflow-y-hidden"
        style={{
          background: T.surface,
          border: `1px solid ${T.hair}`,
          boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset, 0 24px 60px -32px rgba(10,10,10,0.18)`,
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ minWidth: 720 }}>
          {/* Header row — sticky-ish (we don't pin it because the table
              is short enough that scroll-keep-context isn't worth the
              complexity). Pro column highlighted in accSoft. */}
          <div
            className="grid"
            style={{
              gridTemplateColumns: "minmax(220px,1.6fr) repeat(3, minmax(140px,1fr))",
              borderBottom: `1px solid ${T.hair}`,
            }}
          >
            <div className="px-5 py-4">
              <span
                style={{
                  fontFamily: FM, fontSize: 10.5, letterSpacing: "0.16em",
                  textTransform: "uppercase", color: T.ink3, fontWeight: 600,
                }}
              >
                Feature
              </span>
            </div>
            {tiers.map((name, i) => (
              <div
                key={name}
                className="px-5 py-4"
                style={{
                  borderLeft: `1px solid ${T.hair}`,
                  background: i === 1 ? T.accSoft : undefined,
                }}
              >
                <span
                  style={{
                    fontSize: 14, fontWeight: 700,
                    color: i === 1 ? T.acc : T.ink,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {name}
                  {i === 1 && (
                    <span
                      className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded"
                      style={{
                        fontFamily: FM, fontSize: 9.5, background: T.accChip, color: T.acc,
                        letterSpacing: "0.1em", textTransform: "uppercase",
                      }}
                    >
                      popular
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>

          {/* Grouped feature rows */}
          {FEATURE_GROUPS.map((g, gi) => (
            <div key={g.group}>
              {/* Group label band */}
              <div
                className="grid"
                style={{
                  gridTemplateColumns: "minmax(220px,1.6fr) repeat(3, minmax(140px,1fr))",
                  background: T.bgSoft,
                  borderBottom: `1px solid ${T.hair}`,
                  borderTop: gi > 0 ? `1px solid ${T.hair}` : undefined,
                }}
              >
                <div className="px-5 py-2.5">
                  <span
                    style={{
                      fontFamily: FM, fontSize: 10, letterSpacing: "0.18em",
                      textTransform: "uppercase", color: T.ink3, fontWeight: 700,
                    }}
                  >
                    <span style={{ color: T.acc, marginRight: 6 }}>—</span>
                    {g.group}
                  </span>
                </div>
                {tiers.map((_, i) => (
                  <div
                    key={i}
                    className="px-5 py-2.5"
                    style={{
                      borderLeft: `1px solid ${T.hair}`,
                      background: i === 1 ? T.accSoft : undefined,
                    }}
                  />
                ))}
              </div>

              {/* Rows in this group */}
              {g.rows.map((r, ri) => (
                <div
                  key={r.l}
                  className="grid"
                  style={{
                    gridTemplateColumns: "minmax(220px,1.6fr) repeat(3, minmax(140px,1fr))",
                    borderBottom: ri < g.rows.length - 1 ? `1px solid ${T.hair}` : undefined,
                  }}
                >
                  <div className="px-5 py-3.5" style={{ fontSize: 13.5, color: T.ink2 }}>
                    {r.l}
                  </div>
                  {r.v.map((cell, ci) => (
                    <div
                      key={ci}
                      className="px-5 py-3.5 flex items-center"
                      style={{
                        borderLeft: `1px solid ${T.hair}`,
                        background: ci === 1 ? T.accSoft : undefined,
                      }}
                    >
                      <FeatureCell value={cell} highlight={ci === 1} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <p
        className="mt-3 sm:hidden"
        style={{ fontSize: 11, color: T.ink3, fontFamily: FM, letterSpacing: "0.06em" }}
      >
        ← Scroll to see all tiers →
      </p>
    </div>
  )
}

function FeatureCell({ value, highlight }: { value: CellValue; highlight: boolean }) {
  if (value === true) {
    return (
      <span
        style={{
          color: highlight ? T.acc : T.ink,
          display: "inline-flex", alignItems: "center", gap: 6,
          fontSize: 13.5, fontWeight: highlight ? 600 : 500,
        }}
      >
        <Check />
      </span>
    )
  }
  if (value === false) {
    return (
      <span style={{ color: T.ink3, fontSize: 18, lineHeight: 1, fontWeight: 300 }}>—</span>
    )
  }
  if (value === "partial") {
    return (
      <span
        style={{
          color: T.warn,
          display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5,
        }}
      >
        Limited
      </span>
    )
  }
  // Treat "view-only-ish" string values as muted text (not heavy tabular).
  const isMuted = value === "View only" || value === "Standard" || value === "Community"
  return (
    <span
      className={isMuted ? "" : "tabular-nums"}
      style={{
        fontFamily: isMuted ? undefined : FM,
        fontSize: 13,
        color: isMuted ? T.ink3 : (highlight ? T.acc : T.ink),
        fontWeight: highlight && !isMuted ? 700 : 500,
        fontStyle: isMuted ? "italic" : undefined,
      }}
    >
      {value}
    </span>
  )
}
